<template>
  <div class="filters-group">
    <Transition name="toggle">
      <div
        v-show="!filtersToggled"
        class="filters-group__content"
      >
        <location-search
          :contract-start-date="$props.contractStartDate"
          :location="$props.location"
          @search="event => $emit('search', event)"
        />

        <basic-text-input
          v-model="doctorName"
          name="doctorName"
          :label="getTranslation('form.filters.name')"
        />
        <basic-checkbox
          v-model="formData.receivePatients"
          name="receivePatients"
          :label="getTranslation('form.filters.patients')"
          :v="v.receivePatients"
        />
      </div>
    </Transition>

    <div>
      <basic-button
        size="sm"
        hierarchy="secondary"
        :label="
          filtersToggled ? $t('medicalofficesearch.filters.toggle.show') : $t('medicalofficesearch.filters.toggle.hide')
        "
        @click="filtersToggled = !filtersToggled"
      >
        <template #trailing>
          <the-icon
            art="solid"
            :name="filtersToggled ? 'chevron-down' : 'chevron-up'"
          />
        </template>
      </basic-button>
    </div>
  </div>
</template>

<script setup>
  import { reactive, ref, watch } from 'vue'

  import { sameAs } from '@vuelidate/validators'
  import useVuelidate from '@vuelidate/core'

  import useMedicalOffice from '@shared/hooks/useMedicalOffice'

  import LocationSearch from '@shared/components/MedicalSearch/components/LocationSearch'
  import { BasicButton, BasicCheckbox, BasicTextInput } from '@shared/components/Basic/index'

  // HOOKS
  const { getTranslation } = useMedicalOffice()

  // INIT
  const emit = defineEmits(['filter', 'search'])
  defineProps({
    contractStartDate: {
      type: String,
      required: true
    },

    location: {
      type: Object,
      required: true,
    },
  })

  // DATA
  const currentFilters = ref([])
  const doctorName = ref(null)
  const filtersToggled = ref(false)

  const formData = reactive({
    receivePatients: false,
  })

  const rules = {
    receivePatients: { checked: sameAs(true) },
  }

  const v = useVuelidate(rules, formData)

  // METHODS
  function handleInputChange(value) {
    if (!value) {
      currentFilters.value = currentFilters.value.filter(f => f.name !== 'NAME')
      emit('filter', currentFilters.value)
    } else if (value.length >= 3) {
      const inputIndex = currentFilters.value.findIndex(f => f.name === 'NAME')
      if (inputIndex === -1) {
        currentFilters.value.push({ name: 'NAME', value: value })
      } else {
        currentFilters.value[inputIndex].value = value
      }
      emit('filter', currentFilters.value)
    }
  }

  function handleCheckboxChange(isChecked) {
    if (isChecked) {
      currentFilters.value.push({ name: 'PATIENTS', value: true })
    } else {
      currentFilters.value = currentFilters.value.filter(f => f.name !== 'PATIENTS')
    }
    emit('filter', currentFilters.value)
  }

  // WATCHERS
  watch(
    doctorName,
    value => {
      handleInputChange(value)
    },
    { immediate: true }
  )

  watch(
    () => formData.receivePatients,
    value => {
      handleCheckboxChange(value)
    },
    { immediate: true }
  )
</script>

<style scoped>
.toggle-enter-active,
.toggle-leave-active {
  transition: all 0.3s ease-out;
}

.toggle-enter-from,
.toggle-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

.filters-group {
  display: flex;
  flex-direction: column;
  gap: var(--fixed-spacing-fix-04);

  .button {
    width: auto;
  }
}
</style>
